import { useState } from 'react'
import { MoodEmoji } from '~/components/mood-emoji'
import type { PersonCheckin } from '~/lib/api/types'
import styles from './checkin-list.module.scss'

type Props = { checkins: PersonCheckin[] }

export function CheckinList({ checkins }: Props) {
  if (checkins.length === 0) {
    return (
      <p className={styles.empty} aria-live="polite">
        No check-ins in this window.
      </p>
    )
  }

  const recent = checkins.slice(0, 14)
  return (
    <ol className={styles.list} aria-label="Recent check-ins">
      {recent.map(c => (
        <CheckinRow key={c.id} checkin={c} />
      ))}
    </ol>
  )
}

function CheckinRow({ checkin: c }: { checkin: PersonCheckin }) {
  const [open, setOpen] = useState(false)
  const planLine = firstLine(c.plan)

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.summary}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span className={`${styles.date} mono`}>{shortDate(c.date)}</span>
        <span className={styles.mood}>
          <MoodEmoji mood={c.mood} />
        </span>
        <span className={`${styles.hours} mono`}>{c.hours_raw ?? '—'}</span>
        <span className={styles.planLine}>
          {c.is_private && (
            <span className={styles.lock} aria-label="private">
              🔒
            </span>
          )}
          {planLine}
        </span>
        <span className={styles.chev} aria-hidden>
          {open ? '▾' : '▸'}
        </span>
      </button>

      {open && (
        <dl className={styles.detail}>
          {c.mood_note && <Field label="mood note" value={c.mood_note} />}
          {c.plan && <Field label="plan" value={c.plan} multiline />}
          {c.blockers && (
            <Field label="blockers" value={c.blockers} multiline />
          )}
          {c.yesterday_wins && (
            <Field
              label="yesterday's wins"
              value={c.yesterday_wins}
              multiline
            />
          )}
          {c.parse_status === 'failed' && (
            <Field
              label="parse"
              value={`failed — raw: ${c.hours_raw ?? '—'}`}
            />
          )}
          {c.is_private && <Field label="visibility" value="private" />}
        </dl>
      )}
    </li>
  )
}

function Field({
  label,
  value,
  multiline = false,
}: {
  label: string
  value: string
  multiline?: boolean
}) {
  return (
    <>
      <dt className={styles.fieldLabel}>{label}</dt>
      <dd
        className={`${styles.fieldValue} ${multiline ? styles.fieldMulti : ''}`}
      >
        {value}
      </dd>
    </>
  )
}

function firstLine(text: string | null): string {
  if (!text) return '—'
  const idx = text.indexOf('\n')
  const head = idx === -1 ? text : text.slice(0, idx)
  return head.trim() || '—'
}

function shortDate(iso: string): string {
  // 2026-04-29 → "Wed Apr 29"
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y as number, (m as number) - 1, d as number)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}
